import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { VoiceHotwordService, VoiceStatus } from '../../../services/voice-hotword.service';
import { AiAgentService } from '../../../services/API/ai-agent.service';

/**
 * Popover de configuração/diagnóstico de voz, ancorado ao ícone do microfone na topbar.
 * Mostra o estado REAL (modo conversa / gravando / processando / erro), o medidor de
 * volume ao vivo e o seletor de microfone.
 */
@Component({
  selector: 'app-action-voice',
  imports: [CommonModule, MatTooltipModule],
  templateUrl: './action-voice.component.html',
  styleUrl: './action-voice.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActionVoiceComponent {
  private readonly voice = inject(VoiceHotwordService, { optional: true });
  /** Exposto para o template (estado pensando/falando da assistente). */
  readonly ai = inject(AiAgentService, { optional: true });

  @Input() show = false;
  @Output() close = new EventEmitter<void>();

  // Leitura dos signals do serviço (a leitura no template registra a reatividade OnPush).
  get status(): VoiceStatus { return this.voice?.status() ?? 'desligado'; }
  get erro(): string { return this.voice?.erro() ?? ''; }
  get nivel(): number { return this.voice?.nivel() ?? 0; }
  get gravando(): boolean { return this.voice?.gravando() ?? false; }
  get dispositivos(): MediaDeviceInfo[] { return this.voice?.dispositivos() ?? []; }
  get dispositivoSelecionado(): string { return this.voice?.dispositivoSelecionado() ?? ''; }
  get ligado(): boolean { return this.status === 'ouvindo'; }

  // Diagnóstico técnico — piso de ruído, limiar de disparo e log de eventos do VAD.
  get noiseFloor(): number { return this.voice?.noiseFloor() ?? 0; }
  get limiarInicio(): number { return this.voice?.limiarInicio() ?? 0; }
  get eventos(): string[] { return this.voice?.eventos() ?? []; }
  /** Falas detectadas que não foram enviadas por já haver uma resposta em curso — nunca
   *  descartar calado: o usuário precisa saber que tem de repetir. */
  get falasIgnoradas(): number { return this.voice?.falasIgnoradas() ?? 0; }

  get statusLabel(): string {
    const est = this.ai?.estado();
    if (this.ligado && est === 'pensando') return 'Processando sua fala…';
    if (this.ligado && est === 'falando') return 'Assistente respondendo…';
    if (this.ligado && this.gravando) return 'Gravando sua fala…';
    switch (this.status) {
      case 'ouvindo': return 'Modo conversa ativo — pode falar';
      case 'permissao-negada': return 'Permissão de microfone negada';
      case 'sem-suporte': return 'Navegador sem suporte a microfone';
      case 'erro': return 'Erro no microfone';
      default: return 'Voz desligada';
    }
  }

  get statusIcon(): string {
    const est = this.ai?.estado();
    if (this.ligado && est === 'pensando') return 'hourglass_top';
    if (this.ligado && est === 'falando') return 'volume_up';
    if (this.ligado && this.gravando) return 'fiber_manual_record';
    switch (this.status) {
      case 'ouvindo': return 'graphic_eq';
      case 'permissao-negada': return 'mic_off';
      case 'sem-suporte': return 'block';
      case 'erro': return 'error';
      default: return 'mic_off';
    }
  }

  toggle(): void {
    void this.voice?.alternar();
  }

  onDeviceChange(ev: Event): void {
    const id = (ev.target as HTMLSelectElement).value;
    this.voice?.definirDispositivo(id);
  }

  rotuloDispositivo(d: MediaDeviceInfo, index: number): string {
    return d.label || `Microfone ${index + 1}`;
  }

  fechar(): void {
    this.close.emit();
  }
}
